import type { ReactNode } from "react";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { Ago, SeverityBadge } from "../../components/primitives";
import { DASH, formatCount, formatMs, formatPercent, formatStamp } from "../../format";
import type { CameraStats, HistSummary } from "../../types";
import { invalidTone } from "../pipeline/helpers";
import { PizzaRef, ProblemCount } from "../pipeline/shared";
import type { CameraRow } from "./model";

interface DetailItem {
  label: ReactNode;
  value: ReactNode;
  hint?: string;
  mono?: boolean;
}

/** Label / value list that wraps long values instead of cutting them (ids, errors). */
function DetailList({ title, hint, items }: { title: ReactNode; hint?: string; items: DetailItem[] }) {
  return (
    <section className="min-w-0">
      <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" title={hint}>
        {title}
      </h4>
      <dl className="flex flex-col">
        {items.map((item, i) => (
          <div key={i} className="flex min-w-0 items-baseline justify-between gap-3 border-b border-border/50 py-1" title={item.hint}>
            <dt className="shrink-0 text-xs text-muted-foreground">{item.label}</dt>
            <dd className={cn("min-w-0 break-words text-right text-xs tabular-nums", item.mono && "break-all font-mono")}>{item.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** "18:03:12 (5 min ago)" or a dash */
function When({ ts }: { ts: number | null | undefined }) {
  if (!ts) return <span className="text-muted-foreground">{DASH}</span>;
  return (
    <span>
      {formatStamp(ts)} <span className="text-muted-foreground">(<Ago ts={ts} />)</span>
    </span>
  );
}

const triple = (hist: HistSummary | null | undefined) =>
  hist && hist.n ? `${formatMs(hist.p50)} / ${formatMs(hist.p95)} / ${formatMs(hist.max)}` : DASH;

/** Everything the board knows about one camera: ThingsBoard state, branch numbers, this instance's view. */
export function CameraDetails({ row }: { row: CameraRow }) {
  const { t } = useLocalization();
  const { camera } = row;
  const connectivity = camera.connectivity;
  const stats = camera.stats;
  const activity = camera.activity ?? {};

  const thingsboard: DetailItem[] = [
    { label: t("monitoring.cameras.detail.status"), value: t(`cameras.status.${row.kind}`) },
    { label: t("cameras.status.lastActivity"), value: <When ts={connectivity?.last_activity_time} /> },
    { label: t("cameras.status.lastConnect"), value: <When ts={connectivity?.last_connect_time} /> },
    { label: t("cameras.status.lastDisconnect"), value: <When ts={connectivity?.last_disconnect_time} /> },
    { label: t("monitoring.cameras.detail.inactivityAlarm"), value: <When ts={connectivity?.inactivity_alarm_time} /> },
  ];

  const branch: DetailItem[] = stats
    ? [
        { label: t("monitoring.series.orders"), value: formatCount(stats.orders_24h) },
        { label: t("monitoring.series.captured"), value: formatCount(stats.captured_24h) },
        {
          label: t("monitoring.verdict.missing"),
          hint: t("monitoring.cameras.problem.stuckHint"),
          value: <ProblemCount value={stats.missing_24h} tone={(stats.missing_no_photo_24h ?? stats.missing_24h) > 0 ? "nok" : "warn"} />,
        },
        {
          label: t("monitoring.cameras.detail.uncaptured"),
          value: <ProblemCount value={stats.uncaptured} tone={camera.branch?.enable_bb_trigger === false ? "warn" : "nok"} />,
        },
        {
          label: t("monitoring.cameras.detail.invalidShare"),
          value: (
            <span className={cn(invalidTone(stats.invalid_share_24h) === "nok" ? "text-red-400" : invalidTone(stats.invalid_share_24h) === "warn" ? "text-amber-300" : undefined)}>
              {formatPercent(stats.invalid_share_24h)}
            </span>
          ),
        },
        { label: t("monitoring.cameras.detail.lastOrder"), value: <When ts={stats.last_order_at} /> },
        { label: t("monitoring.cameras.seen.lastCapture"), value: <When ts={stats.last_capture_at} /> },
        { label: t("monitoring.cameras.detail.uploadDelay"), value: triple(stats.upload_delay) },
        { label: t("monitoring.cameras.detail.captureToVerdict"), value: triple(stats.capture_to_verdict) },
      ]
    : [{ label: t("monitoring.col.branch"), value: t("monitoring.cameras.unassigned") }];

  const instance: DetailItem[] = [
    {
      label: t("monitoring.cameras.detail.triggers"),
      value: (
        <>
          {formatCount(activity.triggers ?? 0)} (<ProblemCount value={activity.trigger_failures} />)
        </>
      ),
    },
    {
      label: t("monitoring.cameras.detail.lastTrigger"),
      value: activity.last_trigger_at ? (
        <span>
          <span className={activity.last_trigger_ok === false ? "font-semibold text-red-400" : "text-muted-foreground"}>
            {activity.last_trigger_ok === false ? t("monitoring.cameras.instance.failed") : t("monitoring.verdict.ok")}
          </span>
          {typeof activity.last_trigger_ms === "number" && ` · ${formatMs(activity.last_trigger_ms)}`} · <When ts={activity.last_trigger_at} />
        </span>
      ) : (
        DASH
      ),
    },
    { label: t("monitoring.cameras.detail.lastTriggerError"), value: <When ts={activity.last_trigger_error_at} /> },
    {
      label: t("monitoring.cameras.detail.uploads"),
      value: (
        <>
          {formatCount(activity.uploads ?? 0)} (<ProblemCount value={activity.upload_rejections} tone="warn" />)
        </>
      ),
    },
    { label: t("monitoring.cameras.detail.lastUpload"), value: <When ts={activity.last_upload_at} /> },
    {
      label: t("monitoring.cameras.detail.lastUploadAttempt"),
      value: activity.last_upload_attempt_at ? (
        <span>
          <span className={activity.last_upload_status !== 200 ? "font-semibold text-red-400" : "text-muted-foreground"}>
            {activity.last_upload_status ?? DASH}
          </span>{" "}
          · <When ts={activity.last_upload_attempt_at} />
        </span>
      ) : (
        DASH
      ),
    },
    ...(typeof activity.last_upload_delay_ms === "number"
      ? [{ label: t("monitoring.cameras.detail.lastUploadDelay"), value: formatMs(activity.last_upload_delay_ms) }]
      : []),
    { label: t("monitoring.cameras.detail.flagPolls"), value: formatCount(activity.flag_polls ?? 0) },
    { label: t("monitoring.cameras.seen.heartbeat"), hint: t("monitoring.cameras.seen.heartbeatHint"), value: <When ts={activity.last_flags_poll_at} /> },
  ];

  const restart = [
    camera.restart_camera && t("monitoring.cameras.detail.restartCamera"),
    camera.restart_service && t("monitoring.cameras.detail.restartService"),
  ].filter(Boolean);

  const device: DetailItem[] = [
    { label: t("monitoring.cameras.detail.deviceId"), value: camera.id, mono: true },
    { label: t("monitoring.cameras.detail.name"), value: camera.name ?? DASH },
    { label: t("monitoring.cameras.detail.label"), value: camera.label || DASH },
    { label: t("monitoring.cameras.detail.type"), value: camera.type || DASH },
    { label: t("monitoring.cameras.detail.created"), value: camera.created_time ? formatStamp(camera.created_time) : DASH },
    { label: t("monitoring.cameras.detail.branchId"), value: camera.branch_id ?? DASH, mono: true },
    {
      label: t("monitoring.cameras.detail.restartFlags"),
      value: restart.length ? (
        <span className="font-semibold text-amber-300">{restart.join(", ")}</span>
      ) : (
        <span className="text-muted-foreground">{t("monitoring.cameras.detail.restartNone")}</span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-4">
        <DetailList title={t("monitoring.cameras.detail.thingsboard")} items={thingsboard} />
        <DetailList title={t("monitoring.cameras.detail.branch24h")} items={branch} />
        <DetailList title={t("monitoring.cameras.col.instance")} hint={t("monitoring.cameras.instanceHint")} items={instance} />
        <DetailList title={t("monitoring.cameras.detail.device")} items={device} />
      </div>
      {stats && <StuckList stats={stats} />}
      {(activity.last_trigger_error || activity.last_upload_error) && (
        <div className="flex flex-col gap-1.5">
          {activity.last_trigger_error && (
            <ErrorLine label={t("monitoring.cameras.detail.lastTriggerError")} ts={activity.last_trigger_error_at} text={activity.last_trigger_error} />
          )}
          {activity.last_upload_error && (
            <ErrorLine label={t("monitoring.cameras.detail.lastUploadError")} ts={activity.last_upload_error_at} text={activity.last_upload_error} />
          )}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">{t("monitoring.cameras.instanceHint")}</p>
    </div>
  );
}

/** The branch's stuck pizzas: did the camera deliver the photo, and the error that explains the rest. */
function StuckList({ stats }: { stats: CameraStats }) {
  const { t } = useLocalization();
  const stuck = stats.stuck ?? [];
  if (!stuck.length) return null;
  return (
    <section className="min-w-0">
      <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {t("monitoring.pipeline.stuck.title")}
      </h4>
      <ul className="flex flex-col divide-y divide-border/50">
        {stuck.map((pizza) => (
          <li key={pizza.id} className="flex min-w-0 flex-col gap-1 py-1.5">
            <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
              <PizzaRef id={pizza.id} name={pizza.name} className="flex-1" />
              <span className="text-xs">
                <When ts={pizza.capture_at} />
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              <SeverityBadge
                severity={pizza.upload_received ? "warning" : "critical"}
                label={t(pizza.upload_received ? "monitoring.pipeline.stuck.photoYes" : "monitoring.pipeline.stuck.photoNo")}
              />
              <span className="text-[11px] text-muted-foreground">
                {t(pizza.upload_received ? "monitoring.pipeline.stuck.photoYesHint" : "monitoring.pipeline.stuck.photoNoHint")}
              </span>
            </div>
            {pizza.last_event && (
              <div className="break-words font-mono text-[11px] leading-snug text-muted-foreground">{pizza.last_event.message}</div>
            )}
          </li>
        ))}
      </ul>
      {stats.missing_24h > stuck.length && (
        <p className="mt-1 text-[11px] text-muted-foreground">{t("monitoring.pipeline.more", { shown: stuck.length, total: stats.missing_24h })}</p>
      )}
    </section>
  );
}

function ErrorLine({ label, ts, text }: { label: string; ts: number | null | undefined; text: string }) {
  return (
    <div className="rounded-md border border-red-500/30 bg-red-500/10 px-2.5 py-1.5 text-xs">
      <div className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-red-300">
        <span className="font-semibold">{label}</span>
        {ts ? <Ago ts={ts} /> : null}
      </div>
      <div className="mt-0.5 whitespace-pre-wrap break-words font-mono text-[11px] text-red-200/90">{text}</div>
    </div>
  );
}
